import { useState } from "react";
import moment from "moment";
import PropTypes from 'prop-types';

// @mui material components
import { Grid, Card } from "@mui/material";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";

import Map from "components/Maps/Map";

// Data
import { useNavigate, useMatch } from 'react-router-dom';
import DataTable from "components/Tables/DevicesTable";
import { useQuery } from '@tanstack/react-query';

import groupAPI from 'services/api/groups';
import bksAPI from 'services/api/bks';
import clearwaysAPI from 'modules/clearways/api';

import { useAuth0 } from "@auth0/auth0-react";
import NextEvent from "modules/clearways/views/NextEvent";

import { processGroupData } from "utils/group";
import CameraLatest from "components/Camera/CameraLatest";
import { Box, Tab, Tabs } from "@mui/material";
import { tabsClasses } from '@mui/material/Tabs';
import { disableMap } from "utils/map";
import { useSoftUIController } from "hooks/context";
import { wrapper, loadingScreen } from "utils/wrapper";
import { setupGroupTabSelectors } from "utils/setup";
import { groupViewWrapper } from "utils/wrapper";
import { createDataTableRows } from "utils/device";

const mapbox_settings = {
    style: 'mapbox://styles/harrymontano/cm01omrnz000u01omf2uegk93?optimize=true',
    zoom: 17,
    // maxZoom: 5
}
function updateGroup(data) {
    // console.log(data);
    // console.log("TO DO... UPLOAD");
    saveGroupWithID(data.group_id, data);
}

function CustomTabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ px: 1, py: 0, height: '100%' }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}


function ClearwayDeviceGroupView({ map, user }) {
    disableMap(map);

    groupAPI.loadToken(useAuth0());
    bksAPI.loadToken(useAuth0());
    clearwaysAPI.loadToken(useAuth0());
    const [controller] = useSoftUIController();
    const { deviceLayout, userPermissions } = controller;

    const showAdvancedDeviceLevel = userPermissions.indexOf('devices-advanced:view') >= 0;

    const date = moment();
    const month = date.month();
    const year = date.year();
    const navigate = useNavigate();
    const { group_id } = useMatch('clearways/groups/:group_id').params;

    const [tabValue, setTabValue] = useState(0);

    const handleTabClick = (event, newValue) => {
        setTabValue(newValue);
    };

    const onRowClicked = function (row, event) {
        // console.log(row);
        // console.log(event);
        goToDevicePage(row.original.uuid);
    }

    const goToDevicePage = (uuid) => {
        let path = '../../devices/' + uuid;
        // console.log(navigate)
        navigate(path);
    }

    const devices = useQuery({
        queryKey: ['getAllDevices'],
        queryFn: async () => {
            return await bksAPI.getAllDevices();
        }
    });

    const group = useQuery({
        queryKey: ['getGroupWithID', group_id],
        queryFn: async () => {
            return await groupAPI.getGroupWithID(group_id);
        }
    });

    const clearway = useQuery({
        queryKey: ['getClearwaysWithYearAndMonth', year, month],
        queryFn: async () => {
            return await clearwaysAPI.getClearwaysWithYearAndMonth(year, month);
        }
    });

    if (group.isLoading || devices.isLoading || clearway.isLoading) {
        return loadingScreen(deviceLayout, true);
    }

    if (group.error || devices.error || clearway.error) {
        return wrapper("Error!");
    }

    if (!group.data || !devices.data || !clearway.data) {
        return loadingScreen(deviceLayout, false);
    }

    var group_data = processGroupData(group.data, devices.data, showAdvancedDeviceLevel);
    const data_table_data = createDataTableRows(group_data.devices, showAdvancedDeviceLevel);

    if (!group_data.all_updated) {
        var polygon_colour = "#ff667c";
    }
    else if (group_data.devices_monitored > 0 && showAdvancedDeviceLevel) {
        var polygon_colour = "#edaa00";
    }
    else {
        var polygon_colour = "#58ba2d";
    }

    const tabGeneration = () => {
        var tabs = setupGroupTabSelectors(userPermissions, group_data);

        return tabs.map(function (row, index) {
            return <Tab key={index} sx={{ minWidth: "fit-content !important", padding: '12px !important' }} label={row} {...a11yProps(index)} />
        });
    }

    // console.log(group.data)
    return wrapper((
        <SoftBox pb={1} sx={{ height: 'auto', font: '12px / 20px Helvetica Neue, Arial, Helvetica, sans-serif' }}>
            <Grid container>
                <Grid item xxs={12}>
                    <SoftBox p={1}>
                        <Tabs value={tabValue} onChange={handleTabClick} aria-label="DeviceView Tabs"
                            allowScrollButtonsMobile
                            variant="scrollable"
                            scrollButtons
                            sx={{
                                [`& .${tabsClasses.scrollButtons}`]: {
                                    '&.Mui-disabled': { opacity: 0 },
                                },
                                '& .MuiTabs-indicator': {
                                    top: '3px',
                                    left: tabValue == 0 ? '1px !important' : '0px',
                                    height: 'calc(100% - 6px)'
                                }
                            }}
                        >
                            {tabGeneration()}
                        </Tabs>
                    </SoftBox>
                </Grid>
            </Grid>
            <CustomTabPanel value={tabValue} index={setupGroupTabSelectors(userPermissions, group_data, "Overview")}>
                {groupViewWrapper(
                    (
                        <Card sx={{ minHeight: 'calc(100svh - 10.25rem - 16px)', height: '100%' }}>
                            <SoftBox p={2} lineHeight={1} mb={2}>
                                <SoftTypography pl={1} pt={1} variant="h4" textTransform="capitalize">
                                    {group.data.name}
                                </SoftTypography>
                                <SoftTypography pl={1} pt={1} variant="h6" fontWeight="semi-bold" color="text">
                                    {group_data.devices.filter(d => d.active).length + " Active Device" + (group_data.devices.filter(d => d.active).length != 1 ? "s" : "")}
                                </SoftTypography>
                                {showAdvancedDeviceLevel && (
                                    <SoftTypography pl={1} pt={1} variant="caption" fontWeight="regular" color="text">
                                        {group_data.devices.filter(d => !d.active).length + " Reserved Device" + (group_data.devices.filter(d => !d.active).length != 1 ? "s" : "")}
                                    </SoftTypography>
                                )}
                                <DataTable table={data_table_data} user={user} showAdvancedDeviceLevel={showAdvancedDeviceLevel} onRowClicked={(row, event) => onRowClicked(row, event)} canSearch />
                            </SoftBox>
                        </Card>
                    ),
                    (
                        <SoftBox display="flex" flexDirection="column" sx={{ height: '100%' }}>
                            <SoftBox sx={{ minHeight: '200px', height: '100%' }} pb={1}>
                                <Card onClick={(e) => { e.stopPropagation() }} sx={{ minHeight: '200px', height: '100%' }}>
                                    <Map selected_group={group_data} parent_group={group_data.parent_group} allow_devices={true} colour={polygon_colour} settings={mapbox_settings} padding={30} />
                                </Card>
                            </SoftBox>
                            <SoftBox sx={{ height: '100%' }}>
                                <SoftBox height="100%" onClick={(e) => { e.stopPropagation() }}>
                                    <NextEvent data={clearway.data} group_name={group_data.name} groupID={group_id} />
                                </SoftBox>
                            </SoftBox>
                        </SoftBox>
                    ),
                    deviceLayout
                )}

            </CustomTabPanel>
            <CustomTabPanel value={tabValue} index={setupGroupTabSelectors(userPermissions, group_data, "Camera Feeds")}>
                <Card sx={{ minHeight: 'calc(100svh - 10.25rem - 16px)', height: '100%' }}>
                    <Grid container>
                        {group.data.devices.filter(d => d.hardware_type == 'bk-camera').map(function (row, index) {
                            return (<Grid xxs={12} sm={6} m={4} lg={3} xl={2} key={index}>
                                <Card>
                                    <SoftBox p={1} pb={0}>
                                    <SoftTypography pl={1} pt={1} variant="h6" fontWeight="regular" color="text" textAlign="center">
                                        eMinor {row.name}
                                    </SoftTypography>
                                    {/* <SoftTypography pl={1} pt={1} variant="h6" fontWeight="regular" color="text">
                                        {row.serial_number}
                                    </SoftTypography> */}
                                    <CameraLatest uuid={row.uuid} no_padding={true} />
                                    </SoftBox>
                                </Card>
                            </Grid>)
                        })}
                    </Grid>
                </Card>
            </CustomTabPanel>
            <CustomTabPanel value={tabValue} index={setupGroupTabSelectors(userPermissions, group_data, "Group Settings")}>
                <SoftTypography variant="h4">Group Settings</SoftTypography>
            </CustomTabPanel>
        </SoftBox>),
        deviceLayout, true
    );
}

ClearwayDeviceGroupView.propTypes = {
    map: PropTypes.object,
    user: PropTypes.object
}

export default ClearwayDeviceGroupView;